import { type SyntheticEvent, useRef } from 'react';

import { Link } from '@tanstack/react-router';

import { Button } from '@nesso/ui/primitives/button';
import { FieldError } from '@nesso/ui/primitives/field';
import { InputField } from '@nesso/ui/primitives/input-field';

type Props = {
    email: string;
    error?: string;
    formError?: string;
    isSubmitting: boolean;
    setEmail: (value: string) => void;
    onEmailBlur: () => void;
    onSubmit: (event: SyntheticEvent<HTMLFormElement>) => Promise<'email' | undefined>;
};

export function RequestPasswordResetForm({
    email,
    error,
    formError,
    isSubmitting,
    setEmail,
    onEmailBlur,
    onSubmit,
}: Props) {
    const emailRef = useRef<HTMLInputElement>(null);

    const handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
        const fieldToFocus = await onSubmit(event);
        if (fieldToFocus === 'email') emailRef.current?.focus();
    };

    return (
        <form className="flex flex-col gap-6" noValidate onSubmit={handleSubmit}>
            <InputField
                ref={emailRef}
                id="request-password-reset-email"
                type="email"
                label="Email"
                autoComplete="email"
                value={email}
                disabled={isSubmitting}
                error={error}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={onEmailBlur}
            />

            {formError ? <FieldError>{formError}</FieldError> : null}

            <div className="flex flex-col gap-6">
                <Button
                    type="submit"
                    size="lg"
                    variant="default"
                    className="w-full"
                    disabled={isSubmitting}
                >
                    Send Instructions
                </Button>
                <Button asChild variant="link" size="sm" className="w-full">
                    <Link to="/login">Return to Login</Link>
                </Button>
            </div>
        </form>
    );
}
